/* eslint-disable no-console */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TextField, Button, Typography, InputAdornment, IconButton, Alert } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { toast, ToastContainer } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import 'react-toastify/dist/ReactToastify.css';
import '../../App.css';
import logo from '../../assets/logo.svg';
import Loader from '../loader/Loader';
import AuthBackground from './AuthBackground';
import AuthContainer from './AuthContainer';
import { logout } from '../../store/slices/authSlice';
import { setPassword } from '../../apis/resetPassword';
import { passwordValidationSchema } from './Signup';
import { extractErrorMessage } from '../../lib/utils';

// Validation schema - using password rules from Signup
const validationSchema = Yup.object({
    password: passwordValidationSchema,
    confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
        .required('Please confirm your password'),
});

const SetPassword = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isValidToken, setIsValidToken] = useState(false);

    // Get token as a query parameter from url
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    // Decode the token (since it's URL-encoded)
    const decodedToken = token ? decodeURIComponent(token) : null;

    // Auto-logout if user is already logged in
    useEffect(() => {
        const storedUser = localStorage.getItem('authUser');
        const storedToken = localStorage.getItem('authToken');

        if (storedUser || storedToken) {
            dispatch(logout());
            localStorage.removeItem('authUser');
            localStorage.removeItem('authToken');
            localStorage.removeItem('refreshToken');
        }

        // Check if token is present
        if (!decodedToken) {
            toast.error('Invalid or missing reset password link. Please request a new password reset link.');
            setTimeout(() => {
                navigate('/login');
            }, 2000);
        } else {
            setIsValidToken(true);
        }
    }, [dispatch, navigate, decodedToken]);

    const formik = useFormik({
        initialValues: {
            password: '',
            confirmPassword: '',
        },
        validationSchema,
        onSubmit: async (values) => {
            if (!isValidToken || !decodedToken) return;

            try {
                await setPassword({
                    token: decodedToken,
                    new_password: values.password,
                });

                toast.success('Password has been set successfully. You can now login with your password.');
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            } catch (error) {
                console.error('Set password error:', error);
                toast.error(extractErrorMessage(error, 'Failed to set password. Please try again.'));
            }
        },
    });

    if (!isValidToken) {
        return (
            <div className="login_container min-vh-100">
                <AuthBackground />
                <div className="content_container">
                    <div className="content_box">
                        <img src={logo} alt="Logo" className="logo" />
                        <AuthContainer>
                            <Alert severity="error">Invalid set password link. Redirecting to login...</Alert>
                        </AuthContainer>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="login_container min-vh-100">
            <AuthBackground />
            <div className="content_container">
                <Loader isLoading={formik.isSubmitting} />
                <div className="content_box">
                    <img src={logo} alt="Logo" className="logo" />
                    <Typography variant="body1" textAlign="center" marginTop={6} fontWeight={700}>
                        Set Your Password
                    </Typography>

                    <AuthContainer>
                        <Alert severity="info" sx={{ marginBlock: 2 }}>
                            Email verified successfully. Please enter your password below.
                        </Alert>

                        <form onSubmit={formik.handleSubmit}>
                            <div className="input_group mb-3">
                                <label className="input_label"> Password</label>
                                <TextField
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    placeholder="Enter your password"
                                    value={formik.values.password}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.password && Boolean(formik.errors.password)}
                                    helperText={formik.touched.password && formik.errors.password}
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                    size="small"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </div>

                            <div className="input_group mb-4">
                                <label className="input_label">Confirm Password</label>
                                <TextField
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    name="confirmPassword"
                                    placeholder="Confirm your password"
                                    value={formik.values.confirmPassword}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                                    helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    edge="end"
                                                    size="small"
                                                >
                                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </div>

                            <Button type="submit" variant="contained" disabled={formik.isSubmitting} fullWidth>
                                {formik.isSubmitting ? 'Submitting...' : 'Submit'}
                            </Button>
                        </form>
                    </AuthContainer>
                    <p className="copyright">© Pratham Software. All rights reserved.</p>
                </div>
            </div>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                style={{ zIndex: 9999 }}
            />
        </div>
    );
};

export default SetPassword;
